import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import Header from "../../commonComponents/header/Header.js";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { initInterviewId } from "../../../utils/decryptURL.js";
import useNetworkStability from "../../../services/useNetworkStability";

// images
import interViewLoinImg from "../../../assets/images/login-page-left-img.svg";
import emailImg from "../../../assets/images/email-icon.svg";
import phoneImg from "../../../assets/images/phone-otp.svg";
import arrowImg from "../../../assets/images/arrow-white-icon.svg";
import { useWelcome } from "../../../contexts/WelcomeContext";
import CommonSnackbar from "../../commonComponents/snackbar/CommonSnackbar.js";
import Footer from "../../commonComponents/footer/Footer.js";
import LoadingDialog from "../../commonComponents/loader/LoadingDialog.js";
import AccessDenied from "./AccessDenied.js";
import {
  maskPhoneNumber,
  maskText,
} from "../../commonComponents/helperFunctions/HelperFunction.js";
import { UAParser } from "ua-parser-js";
import { Turnstile } from "@marsidev/react-turnstile";
import { authService } from "../../../services/apiInterviewService";

const Login = () => {
  const parser = new UAParser();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const { setInterviewId } = useWelcome();
  const [snackbar, setSnackbar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasPermission, setHasPermission] = useState(null); // null = checking, true = has permission, false = no permission

  const [phoneForLoader, setPhoneForLoader] = useState("");
  const [emailForLoader, setEmailForLoader] = useState("");
  const [isBrowserSupported, setIsBrowserSupported] = useState(true);
  const [showBrowserModal, setShowBrowserModal] = useState(true);
  const [browserErrorMsg, setBrowserErrorMsg] = useState("");
  const [apiAlive, setApiAlive] = useState(false);
  const [warmingUp, setWarmingUp] = useState(false);
  const [prepSecondsLeft, setPrepSecondsLeft] = useState(null); // countdown seconds (120)
  const [warmupFailed, setWarmupFailed] = useState(false); // show busy message after countdown
  const [urlReady, setUrlReady] = useState(false);
  const [urlValid, setUrlValid] = useState(false);
  const [captchaMessage, setCaptchaMessage] = useState(
    "This platform uses a security service to protect against malicious bots. We are verifying you are not a bot.",
  );

  const [initialFormValues, setInitialFormValues] = useState({
    email: "",
    phoneNumber: "",
  });
  const isPhone = sessionStorage.getItem("isPhone");
  const isDirectAssessment = isPhone === "False" || isPhone === "false"; // Handle both boolean and string representations
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0); // used to reset widget
  const [dialCode, setDialCode] = useState("91"); // country dial code
  const [localPhone, setLocalPhone] = useState(""); // local digits only
  const [countryIso, setCountryIso] = useState("in"); // country iso2
  const getNetworkMessage = (netStatus, latencyMs, lossPct) => {
    if (netStatus === "offline") {
      return {
        title: "No internet connection",
        desc: "Please reconnect to Wi-Fi or mobile data to continue.",
      };
    }

    if (lossPct >= 60) {
      return {
        title: "Internet keeps disconnecting",
        desc: "Please switch to a stable network to continue the assessment.",
      };
    }

    if (lossPct >= 30) {
      return {
        title: "Connection is very unstable",
        desc: "Your internet is dropping frequently. Please switch networks.",
      };
    }

    if (lossPct >= 15 || latencyMs > 2500) {
      return {
        title: "Connection is weak",
        desc: "You may face delays or interruptions. Move closer to Wi-Fi or switch networks.",
      };
    }

    if (lossPct >= 5 || latencyMs > 1200) {
      return {
        title: "Connection is slightly unstable",
        desc: "You may notice small delays. A stable connection is recommended.",
      };
    }

    return {
      title: "Connection looks good",
      desc: "",
    };
  };
  const { netStatus, latencyMs, lossPct } = useNetworkStability({
    //  BEST: replace this with your frontend-hosted ping file
    pingUrl: `${window.location.origin}/ping.txt`,
    // pingUrl: "https://needles-and-haystacks.web.app/ping.txt",
    intervalMs: 6000,
    timeoutMs: 7000,
  });

  useEffect(() => {
    const checkBrowserSupport = () => {
      const parser = new UAParser();
      const result = parser.getResult();

      const browserName = result.browser.name || "";
      const browserVersion = result.browser.version || "";

      const isChrome = browserName === "Chrome";
      const isEdge = browserName === "Edge" || browserName === "Chromium";

      if (!isChrome && !isEdge) {
        setIsBrowserSupported(false);

        const msg =
          "Only Google Chrome and Microsoft Edge are supported for this assessment. Please switch your browser.";

        setBrowserErrorMsg(msg);

        setSnackbar({
          message: msg,
          type: "failure",
          persistent: true,
        });

        return false;
      }

      setIsBrowserSupported(true);
      return true;
    };

    checkBrowserSupport();
  }, []);
  useEffect(() => {
    const initUrlParams = async () => {
      try {
        const params = new URLSearchParams(window.location.search);

        const urlInterview = params.get("assessmentId");
        const urlCompany = params.get("companyId");

        // ❌ No URL params → invalid
        if (!urlInterview || !urlCompany) {
          setUrlValid(false);
          setUrlReady(true);
          return;
        }

        // decrypt and store
        await initInterviewId();

        // check session storage AFTER decrypt
        const storedInterview = sessionStorage.getItem("interviewId");
        const storedCompany = sessionStorage.getItem("companyId");

        if (!storedInterview || !storedCompany) {
          const alreadyReloaded = sessionStorage.getItem("url_reload_done");

          if (!alreadyReloaded) {
            sessionStorage.setItem("url_reload_done", "true");
            window.location.reload();
            return;
          }

          setUrlValid(false);
        } else {
          setUrlValid(true);
        }
      } catch (err) {
        console.error("URL init failed:", err);
        setUrlValid(false);
      } finally {
        setUrlReady(true);
      }
    };

    initUrlParams();
  }, []);
  useEffect(() => {
    let isMounted = true; // prevent setState after unmount

    const checkPermission = async () => {
      const preserveKeys = ["company_Id", "interview_Id"];
      Object.keys(sessionStorage).forEach((key) => {
        if (!preserveKeys.includes(key)) {
          sessionStorage.removeItem(key);
        }
      });

      const params = new URLSearchParams(window.location.search);
      const interviewId = params.get("assessmentId");

      if (!interviewId) {
        setHasPermission(false);
        return;
      }

      const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      const warmupUrl = async (url, maxAttempts = 6, timeoutMs = 15000) => {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

            const res = await fetch(url, {
              method: "GET",
              signal: controller.signal,
              cache: "no-store",
            });

            clearTimeout(timeoutId);

            if (res.ok) return true;
          } catch (err) {
            // ignore and retry
          }

          await delay(attempt * 1000); // 1s,2s,3s..
        }
        return false;
      };

      // check both servers once (no retries here)
      const checkBothOnce = async () => {
        try {
          const controller1 = new AbortController();
          const t1 = setTimeout(() => controller1.abort(), 10000);

          const controller2 = new AbortController();
          const t2 = setTimeout(() => controller2.abort(), 10000);

          const server1Url = `${process.env.REACT_APP_API_BASE_URL_WITHOUTAPI}/health-check/`;
          const server2Url = `${process.env.REACT_APP_API_ROOT_URL}/health`;

          const [r1, r2] = await Promise.allSettled([
            fetch(server1Url, {
              method: "GET",
              signal: controller1.signal,
              cache: "no-store",
            }),
            fetch(server2Url, {
              method: "GET",
              signal: controller2.signal,
              cache: "no-store",
            }),
          ]);

          clearTimeout(t1);
          clearTimeout(t2);

          const ok1 = r1.status === "fulfilled" && r1.value.ok;
          const ok2 = r2.status === "fulfilled" && r2.value.ok;

          return ok1 && ok2;
        } catch (e) {
          return false;
        }
      };

      try {
        await initInterviewId();
        if (!isMounted) return;
        setHasPermission(true);
        // Commented out to allow health check to proceed regardless of network status
        // const allowProceed = isNetworkGoodEnough(netStatus, latencyMs, lossPct);
        // if (!allowProceed) {
        //   setApiAlive(false);
        //   setWarmingUp(false);
        //   return;
        // }

        // overlay on
        setWarmingUp(true);
        setApiAlive(false);
        setWarmupFailed(false);
        setPrepSecondsLeft(null);

        // First phase: 6 retry warmup in parallel (your original logic)
        const server1Url = `${process.env.REACT_APP_API_BASE_URL_WITHOUTAPI}/health-check/`;
        const server2Url = `${process.env.REACT_APP_API_ROOT_URL}/health`;

        const [server1Alive, server2Alive] = await Promise.all([
          warmupUrl(server1Url, 6, 15000),
          warmupUrl(server2Url, 6, 15000),
        ]);

        if (!isMounted) return;

        const bothAliveInitial = server1Alive && server2Alive;

        //  If both are alive -> remove overlay immediately
        if (bothAliveInitial) {
          setApiAlive(true);
          setWarmingUp(false);
          return;
        }

        //  Second phase: 2-min countdown + background checks
        setPrepSecondsLeft(120);

        const endAt = Date.now() + 120 * 1000;
        let healthyFound = false;

        // countdown tick
        const countdownInterval = setInterval(() => {
          const remainingMs = endAt - Date.now();
          const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));

          if (!isMounted) return;

          setPrepSecondsLeft(remainingSec);

          if (remainingSec <= 0) {
            clearInterval(countdownInterval);
          }
        }, 1000);

        // background check every 6 seconds until healthy or time ends
        const pollInterval = setInterval(async () => {
          if (Date.now() > endAt || healthyFound) return;

          const healthy = await checkBothOnce();
          if (!isMounted) return;

          if (healthy) {
            healthyFound = true;

            setApiAlive(true);
            setWarmingUp(false);
            setPrepSecondsLeft(null);

            clearInterval(pollInterval);
            clearInterval(countdownInterval);
          }
        }, 6000);

        // final timeout (after 2 min)
        setTimeout(() => {
          if (!isMounted) return;

          //  If still not healthy after 2 mins
          if (!healthyFound) {
            setWarmupFailed(true); // show busy message
          }

          clearInterval(pollInterval);
          clearInterval(countdownInterval);
        }, 120 * 1000);
      } catch (error) {
        console.error("Error initializing interviewId:", error);
        if (!isMounted) return;
        setHasPermission(false);
        setApiAlive(false);
        setWarmingUp(false);
        setWarmupFailed(true);
      }
    };

    checkPermission();

    // remove old session data
    sessionStorage.removeItem("screenup_email");
    sessionStorage.removeItem("flow_type");

    // Load stored login info
    const storedEmail = sessionStorage.getItem("login_email") || "";
    const storedPhone = sessionStorage.getItem("login_phone") || "";

    setInitialFormValues({
      email: storedEmail,
      phoneNumber: storedPhone,
    });

    return () => {
      isMounted = false;
    };
  }, []); // Empty array - only run once on mount to prevent re-renders

  useEffect(() => {
    if (!urlReady) return;

    const params = new URLSearchParams(window.location.search);
    const urlInterview = params.get("assessmentId");
    const urlCompany = params.get("companyId");

    const sessionInterview = sessionStorage.getItem("interviewId");
    const sessionCompany = sessionStorage.getItem("companyId");

    //  If URL params missing → DO NOT refresh
    if (!urlInterview || !urlCompany) {
      return;
    }

    //  If URL exists but sessionStorage not ready → refresh once
    if (!sessionInterview || !sessionCompany) {
      const alreadyReloaded = sessionStorage.getItem("url_reload_done");

      if (!alreadyReloaded) {
        sessionStorage.setItem("url_reload_done", "true");
        window.location.reload();
      }
    }
  }, [urlReady]);

  // Get stored values from sessionStorage if they exist
  const getInitialValues = () => {
    const storedEmail = sessionStorage.getItem("login_email") || "";
    const storedPhone = sessionStorage.getItem("login_phone") || "";
    return {
      email: storedEmail,
      phoneNumber: storedPhone,
    };
  };

  const initialValues = getInitialValues();
  const isNetworkGoodEnough = (netStatus, latencyMs, lossPct) => {
    if (netStatus === "offline") return false;

    const MAX_LATENCY = 15000;
    const MAX_LOSS = 60;

    const lat = typeof latencyMs === "number" ? latencyMs : null;
    const loss = typeof lossPct === "number" ? lossPct : null;

    // allow until we have measurements
    if (lat === null || loss === null) return true;

    return lat <= MAX_LATENCY && loss <= MAX_LOSS;
  };
  const validationSchema = Yup.object({
    email: Yup.string()
      .trim()
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i,
        "Enter a valid email",
      )
      .required("Email is required"),

    phoneNumber:
      isPhone === "False" || isPhone === "false"
        ? Yup.string().notRequired()
        : Yup.string()
            .trim()
            .matches(/^\+?[1-9]\d{9,14}$/, "Enter a valid phone number")
            .required("Phone number is required"),
  });
  const isCaptchaDisabled = process.env.REACT_APP_DISABLE_CAPTCHA === "true";
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const interviewId = sessionStorage.getItem("interviewId");
      const companyId = sessionStorage.getItem("companyId");

      if (!interviewId || !companyId) {
        setSnackbar({
          message: "Session expired. Please reload the assessment link.",
          type: "failure",
        });
        setSubmitting(false);
        return;
      }

      // Validate captcha

      if (!turnstileToken && !isCaptchaDisabled) {
        setSnackbar({
          message: "Please complete the security verification.",
          type: "failure",
        });
        setSubmitting(false);
        return;
      }

      setLoading(true);

      // store login
      sessionStorage.removeItem("screenup_email");
      sessionStorage.removeItem("flow_type");
      sessionStorage.setItem("login_email", values.email);
      sessionStorage.setItem("login_phone", values.phoneNumber);

      //  send OTP directly
      const response = await authService.sendOTP(
        values.email,
        values.phoneNumber,
      );

      setSnackbar({
        message: "OTPs sent successfully!",
        type: "success",
      });

      navigate("/screenup-otp");

      //  go directly to OTP screen
    } catch (error) {
      setSnackbar({
        message: error.message || "Failed to send OTP.",
        type: "failure",
      });
    } finally {
      setLoading(false);
      setSubmitting(false);
      setTurnstileToken(null);
      setTurnstileKey((k) => k + 1);
    }
  };
  if (!urlReady) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Loading assessment...</p>
      </div>
    );
  }

  if (!urlValid) {
    return <AccessDenied />;
  }
  // Show loading state while checking permission
  if (hasPermission === null) {
    return (
      <div className="flex flex-col min-h-screen bg-white pt-4 px-4">
        <Header />
        <div className="flex-1 flex justify-center items-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-logoColor mx-auto mb-4"></div>
            <p className="text-fontColor text-lg">Verifying access...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  // mobile detection AFTER hooks (safe)
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  const isMobileDevice =
    /android|iphone|ipad|ipod|mobile|blackberry|iemobile|opera mini/i.test(
      ua.toLowerCase(),
    ) || window.innerWidth < 768;

  // ✅ mobile block BEFORE app logic renders
  if (isMobileDevice) {
    return (
      <div className="fixed inset-0 z-[999999] bg-white flex flex-col items-center justify-center text-center px-6">
        <label className="  text-[20px] font-bold text-fontColor mb-4">
          Desktop Required
        </label>

        <p className="text-fontColor text-base max-w-[420px]">
          This assessment platform is not supported on mobile browsers.
          <br />
          <br />
          Please open this link on a <b>desktop or laptop</b> to continue.
        </p>
      </div>
    );
  }
  // Show permission denied screen
  if (hasPermission === false) {
    return <AccessDenied />;
  }
  const msg = getNetworkMessage(netStatus, latencyMs, lossPct);
  const allowProceedTop = isNetworkGoodEnough(netStatus, latencyMs, lossPct);
  // Render normal login form if permission is granted
  return (
    <div className="min-h-screen flex flex-col bg-white pt-4 px-4">
      {loading && (
        <LoadingDialog
          title="Sending Verification Code"
          primaryMSG="Sending One-Time Passcode to your"
          email={
            <span className="loading-highlight">
              {maskText(emailForLoader)}
            </span>
          }
          phone={
            !isDirectAssessment ? (
              <span className="loading-highlight">
                {maskPhoneNumber(phoneForLoader)}
              </span>
            ) : null
          }
          bottomMsg="Just a moment."
        />
      )}
      {snackbar && (
        <CommonSnackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={snackbar.persistent ? undefined : () => setSnackbar(null)}
        />
      )}

      <Header />
      {/* Network status banner */}
      {!allowProceedTop && netStatus !== "checking" && (
        <div className="w-full flex justify-center mt-3">
          <div
            className={`w-[95%] md:w-[520px] rounded-xl px-4 py-3 text-sm font-medium ${
              netStatus === "offline"
                ? "bg-red-50 text-red-700 border border-red-200"
                : netStatus === "unstable"
                  ? "bg-orange-50 text-orange-700 border border-orange-200"
                  : "bg-yellow-50 text-yellow-700 border border-yellow-200"
            }`}
          >
            {netStatus === "offline" ? (
              <div>
                ❌ <b>No internet connection.</b> Please check Wi-Fi/Mobile
                data.
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <div className="text-2xl leading-none">
                  {netStatus === "offline" ? "❌" : "⚠️"}
                </div>

                <div className="flex-1">
                  <p className="text-base font-extrabold tracking-wide">
                    {msg.title}
                  </p>

                  <p className="text-sm mt-1 font-semibold opacity-95">
                    {msg.desc}
                  </p>

                  <p className="text-xs mt-2 font-medium opacity-80">
                    Please use a stable internet connection to continue the
                    assessment.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {warmingUp && (
        <div className="fixed inset-0 z-[9999] bg-white flex flex-col items-center justify-center px-4">
          {!warmupFailed ? (
            <>
              {/* Loader only while preparing */}
              <div className="animate-spin rounded-full h-14 w-14 border-b-2 border-logoColor mb-5"></div>

              {/* Meaningful messages */}
              <p className="text-fontColor text-lg font-semibold text-center">
                {netStatus === "offline"
                  ? "No internet connection"
                  : netStatus === "unstable" || netStatus === "warning"
                    ? "Network is unstable"
                    : prepSecondsLeft !== null
                      ? "Preparing your assessment environment"
                      : "Getting your assessment ready"}
              </p>

              <p className="text-fontColor text-sm mt-2 opacity-70 text-center max-w-[360px]">
                {netStatus === "offline"
                  ? "Please reconnect to Wi-Fi/Mobile data to continue."
                  : netStatus === "unstable" || netStatus === "warning"
                    ? msg.desc
                    : prepSecondsLeft !== null
                      ? "This may take up to the time shown below. Please don’t refresh the page."
                      : "Connecting securely and starting required services..."}
              </p>
              {/* Countdown */}
              {prepSecondsLeft !== null &&
                isNetworkGoodEnough(netStatus, latencyMs, lossPct) && (
                  <div className="mt-4 text-fontColor text-base font-medium">
                    Should be ready in{" "}
                    <span className="font-bold">
                      {String(Math.floor(prepSecondsLeft / 60)).padStart(
                        2,
                        "0",
                      )}
                      :{String(prepSecondsLeft % 60).padStart(2, "0")}
                    </span>
                  </div>
                )}
            </>
          ) : (
            <>
              {/* No loader here */}
              <p className="text-fontColor text-lg font-semibold text-center">
                We’re facing high traffic right now
              </p>

              <p className="text-fontColor text-sm mt-2 opacity-70 text-center max-w-[360px]">
                The assessment services are taking longer than expected.
                <br />
                Please try again in a few minutes.
              </p>

              {/*  Network note even in failure case */}
              {netStatus !== "stable" && netStatus !== "checking" && (
                <div
                  className={`mt-4 w-full max-w-[420px] rounded-xl px-4 py-3 text-sm font-medium text-center border ${
                    netStatus === "offline"
                      ? "bg-red-50 text-red-700 border-red-200"
                      : netStatus === "unstable"
                        ? "bg-orange-50 text-orange-700 border-orange-200"
                        : "bg-yellow-50 text-yellow-700 border-yellow-200"
                  }`}
                >
                  {netStatus === "offline" ? (
                    <span>
                      ❌ <b>You are currently offline.</b> Please reconnect and
                      try again.
                    </span>
                  ) : (
                    <span>
                      ⚠️ <b>Network may be unstable.</b>{" "}
                      {latencyMs !== null ? `Latency: ${latencyMs}ms` : ""}
                    </span>
                  )}
                </div>
              )}

              <button
                onClick={() => window.location.reload()}
                className="mt-6 px-6 py-2 rounded-xl bg-btnBgColor text-white"
              >
                Retry
              </button>
            </>
          )}
        </div>
      )}

      {!isBrowserSupported && showBrowserModal && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-6 w-[90%] md:w-[450px] text-center">
            <h2 className="text-[18px] font-semibold text-fontColor mb-3">
              <b>Unsupported Browser</b>
            </h2>

            <p className="!text-sm text-fontColor mb-4">
              Only <b>Google Chrome&trade;</b> and <b>Microsoft Edge&trade;</b>{" "}
              are supported for this assessment.
            </p>
            <p>Please switch your browser to continue.</p>
            <br />
            <button
              onClick={() => setShowBrowserModal(false)}
              className="px-6 py-2 rounded-xl bg-btnBgColor text-white"
            >
              OK
            </button>
          </div>
        </div>
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnMount
      >
        {({
          values,
          handleChange,
          handleBlur,
          isValid,
          isSubmitting,
          submitForm,
        }) => {
          const allowProceed = isNetworkGoodEnough(
            netStatus,
            latencyMs,
            lossPct,
          );
          const interviewIdExists = !!sessionStorage.getItem("interviewId");
          const companyIdExists = !!sessionStorage.getItem("companyId");
          const isDisabled =
            loading ||
            isSubmitting ||
            !isValid ||
            !isBrowserSupported ||
            (!turnstileToken && !isCaptchaDisabled) ||
            !interviewIdExists ||
            !companyIdExists;

          return (
            <>
              {/* Main Content */}
              <div className="flex-1 flex flex-col md:flex-row overflow-y-auto ">
                {/* Left Image */}
                <div className="w-full md:w-1/2 flex justify-center items-center px-4 sm:px-8 md:pl-[34px] md:pr-[50px] py-6 md:py-0">
                  <img
                    src={interViewLoinImg}
                    alt="login"
                    className="object-contain h-auto w-full max-w-[500px] md:w-[100%] rounded-3xl"
                  />
                </div>

                {/* Divider */}
                <div className="hidden md:block border-l border-dividerColor my-[5%]" />

                {/* Right Form Section */}
                <div className="w-full md:w-1/2 flex items-center justify-center md:overflow-y-auto custom-scrollbar p-6 sm:p-10 md:p-[70px]">
                  <Form className="w-full">
                    {/* Welcome heading — fluid: 28px mobile to 48px desktop */}
                    <h2 className="font-medium mb-1 text-fontColor leading-tight app-main-title ">
                      Welcome!
                    </h2>

                    {/* Subtitle — fluid: 16px mobile to 24px desktop */}
                    <p className="text-fontColor leading-snug app-body">
                      To begin with your assessment,
                    </p>

                    <p className="text-fontColor leading-snug app-body">
                      {isDirectAssessment
                        ? "please provide your Email ID."
                        : "please provide your Email ID and Mobile Number."}
                    </p>

                    {/* Email input */}
                    <div className="flex items-center gap-2 w-full sm:w-[85%] md:w-[70%] mt-8 md:mt-12">
                      <img
                        src={emailImg}
                        alt="email"
                        className="flex-shrink-0"
                        style={{ height: "clamp(14px, 1.4vw, 18px)" }}
                      />
                      <input
                        type="text"
                        id="email"
                        name="email"
                        placeholder="Email ID"
                        disabled={!isBrowserSupported}
                        value={values.email}
                        onChange={(e) => {
                          handleChange(e);
                          setEmailForLoader(e.target.value);
                        }}
                        onBlur={handleBlur}
                        className="border-b app-textbox border-logoColor focus:outline-none w-full text-fontColor bg-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </div>

                    {/* Phone Number — phone icon | country <select> (own underline) | number <input> (own underline) */}
                    {!isDirectAssessment && (
                      <div className="flex items-end gap-2 w-full sm:w-[85%] md:w-[75%] mt-8 md:mt-12">
                        {/* Phone icon flush with baseline */}
                        <img
                          src={phoneImg}
                          alt="phone"
                          className="flex-shrink-0 mb-[5px]"
                          style={{
                            height: "clamp(14px, 1.4vw, 18px)",
                            width: "clamp(14px, 1.4vw, 18px)",
                          }}
                        />

                        {/* Country code dropdown — flag emoji + dial code, own underline */}
                        <div
                          className="app-textbox flex-shrink-0 border-b border-logoColor"
                          style={{ minWidth: "clamp(68px, 7vw, 100px)" }}
                        >
                          <select
                            disabled={!isBrowserSupported}
                            value={countryIso}
                            onChange={(e) => {
                              const iso = e.target.value;
                              setCountryIso(iso);
                              // map iso2 → dial code
                              const dialMap = {
                                in: "91",
                                us: "1",
                                gb: "44",
                                au: "61",
                                ca: "1",
                                ae: "971",
                                sg: "65",
                                de: "49",
                                fr: "33",
                                jp: "81",
                                cn: "86",
                                br: "55",
                                za: "27",
                                ng: "234",
                                ke: "254",
                                pk: "92",
                                bd: "880",
                                lk: "94",
                                np: "977",
                                my: "60",
                                ph: "63",
                                id: "62",
                                th: "66",
                                vn: "84",
                                kr: "82",
                                nz: "64",
                                it: "39",
                                es: "34",
                                pt: "351",
                                nl: "31",
                                be: "32",
                                ch: "41",
                                se: "46",
                                no: "47",
                                dk: "45",
                                fi: "358",
                                pl: "48",
                                ru: "7",
                                tr: "90",
                                sa: "966",
                                eg: "20",
                                gh: "233",
                                tz: "255",
                                ug: "256",
                                et: "251",
                                mx: "52",
                                ar: "54",
                                co: "57",
                                cl: "56",
                                pe: "51",
                              };
                              const code = dialMap[iso] || "91";
                              setDialCode(code);
                              const full = code + localPhone;
                              handleChange({
                                target: { name: "phoneNumber", value: full },
                              });
                              setPhoneForLoader(full);
                            }}
                            className="w-full app-textbox focus:outline-none bg-transparent text-fontColor appearance-none cursor-pointer pb-[3px]"
                          >
                            {[
                              ["in", "🇮🇳", "91"],
                              ["us", "🇺🇸", "1"],
                              ["gb", "🇬🇧", "44"],
                              ["au", "🇦🇺", "61"],
                              ["ca", "🇨🇦", "1"],
                              ["ae", "🇦🇪", "971"],
                              ["sg", "🇸🇬", "65"],
                              ["de", "🇩🇪", "49"],
                              ["fr", "🇫🇷", "33"],
                              ["jp", "🇯🇵", "81"],
                              ["cn", "🇨🇳", "86"],
                              ["br", "🇧🇷", "55"],
                              ["za", "🇿🇦", "27"],
                              ["ng", "🇳🇬", "234"],
                              ["ke", "🇰🇪", "254"],
                              ["pk", "🇵🇰", "92"],
                              ["bd", "🇧🇩", "880"],
                              ["my", "🇲🇾", "60"],
                              ["ph", "🇵🇭", "63"],
                              ["id", "🇮🇩", "62"],
                              ["th", "🇹🇭", "66"],
                              ["vn", "🇻🇳", "84"],
                              ["kr", "🇰🇷", "82"],
                              ["nz", "🇳🇿", "64"],
                              ["it", "🇮🇹", "39"],
                              ["es", "🇪🇸", "34"],
                              ["mx", "🇲🇽", "52"],
                              ["ar", "🇦🇷", "54"],
                              ["co", "🇨🇴", "57"],
                              ["sa", "🇸🇦", "966"],
                              ["eg", "🇪🇬", "20"],
                              ["tr", "🇹🇷", "90"],
                            ].map(([iso, flag, code]) => (
                              <option key={iso} value={iso}>
                                {flag} +{code}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Local phone digits — own underline */}
                        <div className="flex-1 border-b border-logoColor">
                          <input
                            type="tel"
                            placeholder="Phone Number"
                            disabled={!isBrowserSupported}
                            value={localPhone}
                            onChange={(e) => {
                              const digits = e.target.value.replace(/\D/g, "");
                              setLocalPhone(digits);
                              const full = dialCode + digits;
                              handleChange({
                                target: { name: "phoneNumber", value: full },
                              });
                              setPhoneForLoader(full);
                            }}
                            className="w-full app-textbox focus:outline-none bg-transparent text-fontColor pb-[3px]"
                          />
                        </div>
                      </div>
                    )}
                    {/* Cloudflare Turnstile — checkbox interaction mode */}
                    <div className="mt-8 md:mt-12">
                      <p className="text-fontColor mb-2 app-body">
                        {captchaMessage}
                      </p>

                      <Turnstile
                        key={turnstileKey}
                        siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                        onSuccess={(token) => {
                          setTurnstileToken(token);
                          setCaptchaMessage(
                            "Verification successful. Please click on Proceed.",
                          );
                        }}
                        onExpire={() => {
                          setTurnstileToken(null);
                          setCaptchaMessage(
                            "This platform uses a security service to protect against malicious bots. We are verifying you are not a bot.",
                          );
                        }}
                        onError={() => {
                          setTurnstileToken(null);
                          setTurnstileKey((k) => k + 1);
                          setCaptchaMessage(
                            "This platform uses a security service to protect against malicious bots. We are verifying you are not a bot.",
                          );
                        }}
                      />
                    </div>
                  </Form>
                </div>
              </div>

              {/* Bottom Button (same style as Verification) */}
              <div className="mt-auto w-full flex justify-end pb-4">
                <button
                  type="button"
                  onClick={submitForm}
                  disabled={isDisabled}
                  className="app-button flex items-center px-4 sm:px-6 py-1.5 transition-colors duration-200 bg-btnBgColor text-white disabled:opacity-30 disabled:cursor-not-allowed mr-4 sm:mr-[36px]"
                >
                  Proceed
                  <img src={arrowImg} alt="arrow" className="ml-2" />
                </button>
              </div>
            </>
          );
        }}
      </Formik>

      <Footer />
    </div>
  );
};

export default Login;
